import { create } from 'zustand';

import type { AnalyticsAdapter } from '@/adapters/analytics/types';
import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from '@/constants/analytics';
import {
  type ContactPickerAnalyticsContext,
  type ContactPickerCloseAction,
  type ContactPickerSource,
  formatContactIdsParam,
} from '@/models/contactPickerAnalytics';

export interface MeetingAnalyticsAdapters {
  analytics: AnalyticsAdapter;
}

export interface ContactPickerClosedInput extends ContactPickerAnalyticsContext {
  readonly closeAction: ContactPickerCloseAction;
  readonly contactIds: readonly number[];
}

export interface ContactAttachedInput {
  readonly meetingId: string;
  readonly source: ContactPickerSource;
  readonly contactIds: readonly number[];
  readonly autoPickerOpened: boolean;
}

export interface MeetingAnalyticsActions {
  trackContactPickerShown: (context: ContactPickerAnalyticsContext) => void;
  trackContactPickerClosed: (input: ContactPickerClosedInput) => void;
  trackContactAttached: (input: ContactAttachedInput) => void;
}

export const createMeetingAnalyticsStore = (adapters: MeetingAnalyticsAdapters) =>
  create<MeetingAnalyticsActions>(() => ({
    trackContactPickerShown: ({ meetingId, source, recordingSessionId }) => {
      adapters.analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_CONTACT_MODAL_SHOWN, {
        [ANALYTICS_PARAMS.MEETING_ID]: meetingId,
        [ANALYTICS_PARAMS.SOURCE]: source,
        [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recordingSessionId,
      });
    },
    trackContactPickerClosed: ({
      meetingId,
      source,
      recordingSessionId,
      closeAction,
      contactIds,
    }) => {
      adapters.analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_CONTACT_MODAL_CLOSED, {
        [ANALYTICS_PARAMS.MEETING_ID]: meetingId,
        [ANALYTICS_PARAMS.SOURCE]: source,
        [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recordingSessionId,
        [ANALYTICS_PARAMS.CLICKED_TARGET_LABEL]: closeAction,
        [ANALYTICS_PARAMS.CONTACT_IDS]: formatContactIdsParam(contactIds),
      });
    },
    trackContactAttached: ({ meetingId, source, contactIds, autoPickerOpened }) => {
      adapters.analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_CONTACT_ATTACHED, {
        [ANALYTICS_PARAMS.MEETING_ID]: meetingId,
        [ANALYTICS_PARAMS.SOURCE]: source,
        [ANALYTICS_PARAMS.CONTACT_IDS]: formatContactIdsParam(contactIds),
        [ANALYTICS_PARAMS.CONTACT_COUNT]: contactIds.length,
        [ANALYTICS_PARAMS.AUTO_PICKER_OPENED]: autoPickerOpened,
      });
    },
  }));
